import { createClient } from '@supabase/supabase-js';

// Configuração do Supabase
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
export const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

const supabaseUrl = SUPABASE_URL;
const supabaseAnonKey = SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

const cookieStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined = (() => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

  const hostname = window.location.hostname;
  const isOverpixelOnline = hostname === 'overpixel.online' || hostname.endsWith('.overpixel.online');
  if (!isOverpixelOnline) return undefined;

  const cookieAttrsBase = (() => {
    const attrs: string[] = ['Path=/', 'SameSite=Lax', 'Domain=.overpixel.online'];
    if (window.location.protocol === 'https:') attrs.push('Secure');
    return attrs.join('; ');
  })();

  const parseCookies = (): Record<string, string> => {
    const out: Record<string, string> = {};
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    for (const entry of cookies) {
      const idx = entry.indexOf('=');
      const name = idx >= 0 ? entry.slice(0, idx) : entry;
      const value = idx >= 0 ? entry.slice(idx + 1) : '';
      out[name] = value;
    }
    return out;
  };

  const setCookie = (name: string, encodedValue: string, maxAgeSeconds: number) => {
    document.cookie = `${name}=${encodedValue}; Max-Age=${maxAgeSeconds}; ${cookieAttrsBase}`;
  };

  const removeCookie = (name: string) => {
    document.cookie = `${name}=; Max-Age=0; ${cookieAttrsBase}`;
  };

  const getItem = (key: string) => {
    const cookies = parseCookies();

    const countKey = `${key}.__count`;
    const countRaw = cookies[countKey];
    if (countRaw) {
      const count = Number.parseInt(countRaw, 10);
      if (Number.isFinite(count) && count > 0) {
        let joined = '';
        for (let i = 0; i < count; i++) {
          joined += cookies[`${key}.${i}`] || '';
        }
        try {
          return decodeURIComponent(joined);
        } catch {
          return joined;
        }
      }
    }

    const direct = cookies[key];
    if (direct != null) {
      try {
        return decodeURIComponent(direct);
      } catch {
        return direct;
      }
    }
    return null;
  };

  const setItem = (key: string, value: string) => {
    const maxAge = 60 * 60 * 24 * 365;
    const encoded = encodeURIComponent(value);

    removeItem(key);

    const chunkSize = 3000;
    if (encoded.length <= chunkSize) {
      setCookie(key, encoded, maxAge);
      return;
    }

    const count = Math.ceil(encoded.length / chunkSize);
    setCookie(`${key}.__count`, String(count), maxAge);
    for (let i = 0; i < count; i++) {
      const chunk = encoded.slice(i * chunkSize, (i + 1) * chunkSize);
      setCookie(`${key}.${i}`, chunk, maxAge);
    }
  };

  const removeItem = (key: string) => {
    const cookies = parseCookies();

    const countKey = `${key}.__count`;
    const countRaw = cookies[countKey];
    if (countRaw) {
      const count = Number.parseInt(countRaw, 10);
      if (Number.isFinite(count) && count > 0) {
        for (let i = 0; i < count; i++) {
          removeCookie(`${key}.${i}`);
        }
      }
      removeCookie(countKey);
    } else {
      for (let i = 0; i < 20; i++) {
        removeCookie(`${key}.${i}`);
      }
      removeCookie(countKey);
    }

    removeCookie(key);
  };

  return { getItem, setItem, removeItem };
})();

let lastRefreshFail = 0
let refreshBackoffMs = 0
const sleep = (ms: number) => new Promise(r => setTimeout(r, ms))
const customFetch = async (input: any, init?: RequestInit) => {
  const startTime = performance.now()
  let reqSize = 0
  const urlStr = typeof input === 'string' ? input : (input?.url || '')
  
   // Metric: Egress (Request)
   try {
     reqSize += urlStr.length
     
     if (init) {
       if (init.body) {
         if (typeof init.body === 'string') {
           reqSize += init.body.length
         } else if (init.body instanceof Blob) {
            reqSize += init.body.size
         } else if (init.body instanceof FormData) {
            // Rough estimate for FormData
            reqSize += 1000 
         } else {
            // JSON.stringify if object? fetch init.body is usually string/Blob/Buffer
            reqSize += String(init.body).length
         }
       }
       // Headers estimate
       if (init.headers) {
          reqSize += JSON.stringify(init.headers).length
       }
     }
     supabaseMetrics.counters.egressBytes += reqSize
   } catch (e) { }
 
   const url = urlStr
   const isRefresh = /\/auth\/v1\/token/.test(url) && /grant_type=refresh_token/.test(url)
   const isSupabaseRest = typeof url === 'string' && url.startsWith(String(supabaseUrl))
   const now = Date.now()
   const offline = typeof navigator !== 'undefined' && navigator && navigator.onLine === false
   
   let response: Response | undefined;
 
   if (isRefresh) {
     // Permitir refresh mesmo com a aba oculta para evitar logout por expiração
     if (offline) {
       return new Response(JSON.stringify({ message: 'deferred: offline' }), { status: 503, headers: { 'Content-Type': 'application/json' } })
     }
     if (refreshBackoffMs && (now - lastRefreshFail) < refreshBackoffMs) {
       const wait = refreshBackoffMs - (now - lastRefreshFail)
       if (wait > 0) await sleep(Math.min(wait, 5000))
       // Prosseguir com tentativa de refresh após pequeno atraso, sem retornar 429
     }
     try {
       response = await fetch(input, init as any)
       if (!response.ok && response.status >= 500) {
         lastRefreshFail = Date.now()
         refreshBackoffMs = Math.min(300000, refreshBackoffMs ? refreshBackoffMs * 2 : 10000)
       } else {
         refreshBackoffMs = 0
       }
     } catch (e) {
       lastRefreshFail = Date.now()
       refreshBackoffMs = Math.min(300000, refreshBackoffMs ? refreshBackoffMs * 2 : 10000)
       return new Response(JSON.stringify({ message: 'network-failed' }), { status: 503, headers: { 'Content-Type': 'application/json' } })
     }
   } else if (isSupabaseRest) {
    if (offline) {
      return new Response(JSON.stringify({ message: 'deferred: offline' }), { status: 429, headers: { 'Content-Type': 'application/json' } })
    }
    
    // [FIX] REMOVIDO BLOQUEIO DE BACKGROUND/HIDDEN
    // O bloqueio "hidden" causava erro ERR_ABORTED/429 quando o usuário voltava para a aba ou em algumas operações de background.
    // Agora permitimos requisições REST mesmo em background para garantir sincronização.
    
    // Assegurar que os headers de autenticação estejam presentes
    const headers = new Headers(init?.headers || {})
    if (!headers.has('apikey')) {
      headers.set('apikey', supabaseAnonKey)
    }
    // Se não houver Authorization, adicionar o anon key como fallback, 
    // mas idealmente o cliente Supabase já deve ter adicionado o Bearer token do usuário se estiver logado.
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${supabaseAnonKey}`)
    }
    
    const newInit = { ...init, headers }

    try {
      response = await fetch(input, newInit as any)
    } catch (e) {
      return new Response(JSON.stringify({ message: 'network-failed' }), { status: 503, headers: { 'Content-Type': 'application/json' } })
    }
  } else {
     response = await fetch(input, init as any)
   }
 
   // Metric: Ingress (Response) & Logging
   let resSize = 0
   if (response) {
     try {
       const len = response.headers.get('content-length')
       if (len) {
         resSize = parseInt(len, 10)
         supabaseMetrics.counters.ingressBytes += resSize
       }
     } catch (e) { }
   }

   // Add to Recent Requests Log
   try {
      const duration = performance.now() - startTime
      supabaseMetrics.recentRequests.unshift({
        id: Math.random().toString(36).substring(7),
        timestamp: Date.now(),
        method: init?.method || 'GET',
        url: urlStr,
        status: response?.status,
        reqSize,
        resSize,
        duration
      })
      if (supabaseMetrics.recentRequests.length > 50) {
        supabaseMetrics.recentRequests.pop()
      }
   } catch(e) {}
 
   return response
 }

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    ...(cookieStorage ? { storage: cookieStorage } : {})
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
  ,global: { fetch: customFetch as any }
});

export const supabaseMetrics = {
  counters: {
    select: 0,
    insert: 0,
    delete: 0,
    realtimeEvents: 0,
    storageUploads: 0,
    storagePublicUrl: 0,
    storageSignedUrl: 0,
    egressBytes: 0,
    ingressBytes: 0
  },
  times: {
    selectMs: [] as number[],
    prefetchMs: [] as number[],
    insertBatchMs: [] as number[],
    uploadMs: [] as number[]
  },
  recentRequests: [] as Array<{
    id: string;
    timestamp: number;
    method: string;
    url: string;
    status?: number;
    reqSize: number;
    resSize: number;
    duration: number;
  }>
}
try { (globalThis as any).supabaseMetrics = (globalThis as any).supabaseMetrics || supabaseMetrics } catch {}

// Tipos para o banco de dados
export interface Database {
  public: {
    Tables: {
      users: {
        Row: {
          id: string;
          uid: string;
          email: string | null;
          display_name: string | null;
          photo_url: string | null;
          full_name: string | null;
          phone_number: string | null;
          role: string;
          permissions: any;
          tokens: {
            balance: number;
            totalPurchased: number;
            totalSpent: number;
          };
          usage: {
            downloads: number;
            storageUsed: number;
          };
          subscription: {
            plan: string;
            status: string;
          };
          preferences: {
            theme: string;
            language: string;
            notifications: boolean;
          };
          created_at: string;
          last_login_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          uid: string;
          email?: string | null;
          display_name?: string | null;
          photo_url?: string | null;
          full_name?: string | null;
          phone_number?: string | null;
          role?: string;
          permissions?: any;
          tokens?: any;
          usage?: any;
          subscription?: any;
          preferences?: any;
          created_at?: string;
          last_login_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          uid?: string;
          email?: string | null;
          display_name?: string | null;
          photo_url?: string | null;
          full_name?: string | null;
          phone_number?: string | null;
          role?: string;
          permissions?: any;
          tokens?: any;
          usage?: any;
          subscription?: any;
          preferences?: any;
          created_at?: string;
          last_login_at?: string;
          updated_at?: string;
        };
      };
      token_ledger: {
        Row: {
          id: string;
          user_id: string;
          amount: number;
          reason: string;
          metadata: any;
          idempotency_key: string | null;
          created_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          amount: number;
          reason: string;
          metadata?: any;
          idempotency_key?: string | null;
          created_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          amount?: number;
          reason?: string;
          metadata?: any;
          idempotency_key?: string | null;
          created_at?: string;
        };
      },
      constellations: {
        Row: {
          id: string;
          name: string;
          description: string | null;
          position: any;
          items: any[];
          connections: any[];
          creator_id: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          description?: string | null;
          position?: any;
          items?: any[];
          connections?: any[];
          creator_id?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          description?: string | null;
          position?: any;
          items?: any[];
          connections?: any[];
          creator_id?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      user_progress: {
        Row: {
          id: string;
          user_id: string;
          unlocked_nodes: string[];
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          unlocked_nodes?: string[];
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          unlocked_nodes?: string[];
          created_at?: string;
          updated_at?: string;
        };
      };
      transactions: {
        Row: {
          id: string;
          user_id: string;
          type: string;
          node_id: string | null;
          tokens_cost: number;
          previous_balance: number;
          new_balance: number;
          metadata: any;
          created_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          type: string;
          node_id?: string | null;
          tokens_cost?: number;
          previous_balance?: number;
          new_balance?: number;
          metadata?: any;
          created_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          type?: string;
          node_id?: string | null;
          tokens_cost?: number;
          previous_balance?: number;
          new_balance?: number;
          metadata?: any;
          created_at?: string;
        };
      };
      store: {
        Row: {
          id: string;
          name: string;
          description: string | null;
          price: number | null;
          category: string | null;
          tags: string[] | null;
          status: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          description?: string | null;
          price?: number | null;
          category?: string | null;
          tags?: string[] | null;
          status?: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          description?: string | null;
          price?: number | null;
          category?: string | null;
          tags?: string[] | null;
          status?: string;
          created_at?: string;
          updated_at?: string;
        };
      };
    };
    Views: {
      [_ in never]: never;
    };
    Functions: {
      unlock_star: {
        Args: {
          user_id: string;
          node_id: string;
          tokens_cost: number;
        };
        Returns: {
          success: boolean;
          message: string;
          new_balance: number;
        };
      };
    };
    Enums: {
      [_ in never]: never;
    };
  };
}

// Tipo do cliente Supabase tipado
export type SupabaseClient = typeof supabase;

// Helper para verificar se o Supabase está configurado
export const isSupabaseConfigured = () => {
  return !!(supabaseUrl && supabaseAnonKey);
};

// Helper para obter informações de conexão
export const getSupabaseInfo = () => {
  return {
    url: supabaseUrl,
    configured: isSupabaseConfigured(),
    version: '2.56.0'
  };
};

export default supabase;
